import type { ReceiptData } from "../types/printer.types";

// ─── ESC/POS byte constants ───────────────────────────────────────────────────

const ESC = 0x1b;
const GS  = 0x1d;
const LF  = 0x0a;

// ─── Alignment values ─────────────────────────────────────────────────────────

const ALIGN = {
  LEFT:   0x00,
  CENTER: 0x01,
  RIGHT:  0x02,
} as const;

type AlignValue = (typeof ALIGN)[keyof typeof ALIGN];

/** Max printable dots on a 58mm printer */
const PRINT_WIDTH_PX = 384;
/** Characters per line at normal font size */
const CHARS_PER_LINE = 32;

async function pngBase64ToPixels(
  dataUrl: string,
  maxWidth = PRINT_WIDTH_PX,
): Promise<{ pixels: Uint8Array; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      // Scale down to maxWidth, preserve aspect ratio
      const scale  = Math.min(1, maxWidth / img.naturalWidth);
      const width  = Math.floor(img.naturalWidth  * scale);
      const height = Math.floor(img.naturalHeight * scale);

      const canvas = document.createElement("canvas");
      canvas.width  = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Could not get 2D canvas context"));
        return;
      }

      // White background so transparent PNGs don't become solid black
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      const imageData = ctx.getImageData(0, 0, width, height);
      resolve({
        pixels: new Uint8Array(imageData.data.buffer),
        width,
        height,
      });
    };

    img.onerror = () => reject(new Error("Failed to load PNG image"));
    img.src = dataUrl;
  });
}

// ─── Builder class ────────────────────────────────────────────────────────────

/**
 * Minimal ESC/POS command builder for the VSC MP-58M Pro.
 * 58mm paper · 48mm print area · 384 dots/line · 32 chars/line @ normal size
 */
class EscPosBuilder {
  private cmds: number[] = [];
  private readonly encoder = new TextEncoder();

  /** Append raw bytes */
  private push(...bytes: number[]): this {
    this.cmds.push(...bytes);
    return this;
  }

  /** Encode a string and append its bytes */
  private write(s: string): this {
    this.encoder.encode(s).forEach((b) => this.cmds.push(b));
    return this;
  }

  /** Hardware init — resets all settings */
  init(): this {
    return this.push(ESC, 0x40);
  }

  /** Set text alignment */
  align(a: AlignValue): this {
    return this.push(ESC, 0x61, a);
  }

  /** Bold on/off */
  bold(on: boolean): this {
    return this.push(ESC, 0x45, on ? 0x01 : 0x00);
  }

  /** Print a string (no newline) */
  text(s: string): this {
    return this.write(s);
  }

  line(s = ""): this {
    return this.write(s + "\n");
  }

  feed(n = 1): this {
    for (let i = 0; i < n; i++) this.push(LF);
    return this;
  }

  /** Full-width separator using = characters */
  doubleSeparator(): this {
    return this.line("=".repeat(CHARS_PER_LINE));
  }

  /** Full-width separator using - characters */
  singleSeparator(): this {
    return this.line("-".repeat(CHARS_PER_LINE));
  }

  /**
   * Two-column row padded to CHARS_PER_LINE.
   * If content overflows, right value wraps to next line right-aligned.
   */
  row(left: string, right: string): this {
    const gap = CHARS_PER_LINE - left.length - right.length;
    if (gap >= 1) {
      return this.line(left + " ".repeat(gap) + right);
    }
    return this.line(left).line(right.padStart(CHARS_PER_LINE));
  }

  /**
   * Renders a PNG (RGBA pixel data) as a 1-bit ESC/POS raster image.
   * Uses GS v 0 command.
   */
  image(pixels: Uint8Array, width: number, height: number): this {
    const byteWidth = Math.ceil(width / 8);

    this.push(
      GS, 0x76, 0x30, 0x00,
      byteWidth & 0xff, (byteWidth >> 8) & 0xff,
      height    & 0xff, (height    >> 8) & 0xff,
    );

    for (let y = 0; y < height; y++) {
      for (let bx = 0; bx < byteWidth; bx++) {
        let byte = 0;
        for (let bit = 0; bit < 8; bit++) {
          const x   = bx * 8 + bit;
          const idx = (y * width + x) * 4;
          if (x < width && pixels[idx + 3] > 128) {
            const lum = (pixels[idx] * 299 + pixels[idx + 1] * 587 + pixels[idx + 2] * 114) / 1000;
            if (lum < 128) byte |= 1 << (7 - bit);
          }
        }
        this.push(byte);
      }
    }

    return this;
  }

  cut(): this {
    return this.push(GS, 0x56, 0x41, 0x03);
  }

  build(): Uint8Array {
    return new Uint8Array(this.cmds);
  }
}

// ─── Public helper ────────────────────────────────────────────────────────────

/**
 * Builds a full ESC/POS receipt for the given order.
 * Formatted for Indonesian locale (Rp currency, id-ID number format).
 */
export async function buildReceiptBytes(receipt: ReceiptData): Promise<Uint8Array> {
  const fmt = (n: number): string =>
    "Rp" + n.toLocaleString("id-ID");

  const b = new EscPosBuilder();

  b.init();

  // ── Logo ─────────────────────────────────────────────────────────────────────
  const logo = await pngBase64ToPixels(receipt.logoPngBase64, 200);
  b.align(ALIGN.CENTER).image(logo.pixels, logo.width, logo.height).feed();

  // ── COMPANY NAME ───────────────────────────────────────────────────────────
  b.feed().align(ALIGN.CENTER).line("PMH EKSPEDISI");

  // ── Branch address ───────────────────────────────────────────────────────────
  b.align(ALIGN.CENTER).line(receipt.officeAddress).feed();

  // ── Order meta ───────────────────────────────────────────────────────────────
  b.align(ALIGN.LEFT)
   .doubleSeparator()
   .line(`No. Resi: ${receipt.shippingNumber}`)
   .line(`Tanggal: ${receipt.printDate}`)
   .line(`Status pembayaran: ${receipt.paymentStatus}`)
   .line(`Kasir: ${receipt.cashier ?? "-"}`)
   .doubleSeparator();

  // ── Items ────────────────────────────────────────────────────────────────────
  for (const item of receipt.items) {
    b.line(`Jumlah Barang : ${item.quantity} ${item.unit}`);
  }

  b.singleSeparator();

  // ── Totals ───────────────────────────────────────────────────────────────────
  b.align(ALIGN.LEFT)
   .line(`Subtotal      : ${fmt(receipt.subtotal)}`)
   .line(`Diskon        : ${fmt(receipt.discount)}`)
   .singleSeparator()
   .bold(true).line(`TOTAL TAGIHAN : ${fmt(receipt.totalPayment)}`).bold(false);

   
  // ── Footer ───────────────────────────────────────────────────────────────────
  b.doubleSeparator().align(ALIGN.CENTER)
   .feed()
   .line("Terima kasih!")
   .line("Simpan struk ini sebagai bukti")
   .feed(4)
   .cut();

  return b.build();
}
